import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Computer as ComputerIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  Public as PublicIcon,
  IntegrationInstructions as IntegrationIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ABISystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Automated Broker Interface (ABI)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            CBP Electronic Data Interchange System for Customs Brokers
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Automated Broker Interface (ABI) is U.S. Customs and Border Protection's Electronic Data Interchange (EDI) 
            system that enables customs brokers, importers, and carriers to electronically transmit entry data and receive 
            status updates directly from ACE. As the primary system-to-system interface for commercial trade processing, 
            ABI facilitates high-volume, automated filing of import entries and provides real-time communication between 
            the trade community and CBP.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="ABI System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is ABI"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    An <strong>Electronic Data Interchange (EDI) system</strong> that provides automated, system-to-system 
                    communication between customs brokers and CBP. ABI enables <strong>high-volume electronic filing</strong> of 
                    entry summaries, cargo releases, and related customs documents using standardized message formats (EDIFACT, X12). 
                    Serves as the technical backbone for <strong>90% of U.S. import entries</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses ABI"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Used by <strong>licensed customs brokers</strong> and their software vendors to file entries on behalf of importers. 
                    Over <strong>11,000 licensed brokers</strong> and 200+ software providers utilize ABI for automated filing. Also 
                    accessible to <strong>self-filing importers</strong> with direct filing authorization and carriers for manifest 
                    submissions. Managed by <strong>CBP Office of Trade</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was ABI deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Launched in <strong>1984</strong> as one of CBP's first electronic filing systems. Continuously modernized with 
                    <strong> ACE integration (2010-2016)</strong>, enhanced message sets, and <strong>Global Business Identifier (GBI) 
                    implementation (2020)</strong>. Operates <strong>24/7/365</strong> with scheduled maintenance windows. Processes 
                    over <strong>80,000 transactions daily</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is ABI deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Cloud-based EDI platform</strong> accessible nationwide and internationally by certified ABI participants. 
                    Integrated with <strong>ACE backend systems</strong> for entry processing and all 328 U.S. ports of entry. Supports 
                    <strong> Value-Added Networks (VANs)</strong> and direct TCP/IP connections. Enables filing from broker offices, 
                    software systems, and international locations.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why ABI exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To enable <strong>high-volume, automated trade processing</strong> that would be impossible with manual filing. 
                    Reduces processing time from days to minutes, eliminates paper documents, enables <strong>straight-through 
                    processing</strong> for low-risk shipments, and provides real-time status updates. Critical for <strong>just-in-time 
                    supply chains</strong> and e-commerce operations requiring rapid customs clearance.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core System Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Electronic Entry Filing
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Automated submission of customs entry documentation and supporting data.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Entry Summary (Type 01):</strong> CBP Form 7501 electronic filing</Typography></li>
                <li><Typography variant="body2"><strong>Entry/Immediate Delivery (Type 11):</strong> Cargo release requests</Typography></li>
                <li><Typography variant="body2"><strong>Warehouse Withdrawal (Type 02):</strong> Bonded warehouse entries</Typography></li>
                <li><Typography variant="body2"><strong>In-Bond (Type 61/62/63):</strong> Transit and transportation entries</Typography></li>
                <li><Typography variant="body2"><strong>Informal Entry (Type 86):</strong> Section 321 de minimis shipments</Typography></li>
                <li><Typography variant="body2"><strong>Invoice Data:</strong> Commercial invoice and packing list transmission</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SpeedIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Real-Time Status Updates
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Immediate notifications on entry processing status and CBP actions.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Release Notifications:</strong> Cargo release and hold messages</Typography></li>
                <li><Typography variant="body2"><strong>Exam Requests:</strong> Inspection and examination notifications</Typography></li>
                <li><Typography variant="body2"><strong>Liquidation Status:</strong> Entry liquidation and rate advance notices</Typography></li>
                <li><Typography variant="body2"><strong>Bill Status:</strong> Duty bill generation and payment confirmations</Typography></li>
                <li><Typography variant="body2"><strong>Error Messages:</strong> Reject codes and correction guidance</Typography></li>
                <li><Typography variant="body2"><strong>Query Responses:</strong> Entry status and history lookups</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IntegrationIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Global Business Identifier (GBI) Integration
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Standardized entity identification for trade participants.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Importer of Record:</strong> GBI-based importer identification</Typography></li>
                <li><Typography variant="body2"><strong>Manufacturer ID:</strong> Foreign manufacturer registration and tracking</Typography></li>
                <li><Typography variant="body2"><strong>Consignee Data:</strong> Ultimate consignee identification</Typography></li>
                <li><Typography variant="body2"><strong>Broker Validation:</strong> Licensed broker verification</Typography></li>
                <li><Typography variant="body2"><strong>Carrier Codes:</strong> SCAC and carrier identification</Typography></li>
                <li><Typography variant="body2"><strong>Continuous Bond:</strong> Surety and bond validation</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Compliance & Security
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Built-in validation and security controls for trade compliance.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Data Validation:</strong> Real-time edit checks and error detection</Typography></li>
                <li><Typography variant="body2"><strong>HTS Validation:</strong> Tariff classification verification</Typography></li>
                <li><Typography variant="body2"><strong>PGA Requirements:</strong> Partner agency data element checks</Typography></li>
                <li><Typography variant="body2"><strong>Encryption:</strong> Secure data transmission (TLS/SSL)</Typography></li>
                <li><Typography variant="body2"><strong>Authentication:</strong> Digital certificates and password controls</Typography></li>
                <li><Typography variant="body2"><strong>Audit Trail:</strong> Complete transaction logging and tracking</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Performance */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Scale & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                90%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Entry Market Share
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Of all U.S. import entries
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                80K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Daily Transactions
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Peak: 120K during holidays
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                11K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Licensed Brokers
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Using ABI nationwide
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                &lt;5s
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Response Time
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Real-time processing
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Broker & Importer Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>High-Volume Processing:</strong> Handle thousands of entries daily</Typography></li>
                <li><Typography variant="body2"><strong>Automated Workflows:</strong> Integration with broker management systems</Typography></li>
                <li><Typography variant="body2"><strong>Reduced Errors:</strong> Built-in validation catches mistakes before submission</Typography></li>
                <li><Typography variant="body2"><strong>Faster Release:</strong> Immediate cargo release for compliant shipments</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Cost Efficiency:</strong> Eliminate manual data entry and paper processing</Typography></li>
                <li><Typography variant="body2"><strong>Real-Time Visibility:</strong> Instant status updates and notifications</Typography></li>
                <li><Typography variant="body2"><strong>Scalability:</strong> Handle peak volumes without additional staff</Typography></li>
                <li><Typography variant="body2"><strong>Compliance Support:</strong> Automated checks ensure regulatory compliance</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Technical Specifications */}
      <Card sx={{ mb: 4, bgcolor: '#fff3e0', border: '2px solid #F57C00' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#F57C00' }}>
            Technical Specifications
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Message Formats:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">UN/EDIFACT (International standard)</Typography></li>
                <li><Typography variant="body2">ANSI X12 (North American EDI)</Typography></li>
                <li><Typography variant="body2">Customs-specific message sets</Typography></li>
                <li><Typography variant="body2">XML for modern integrations</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Connectivity Options:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Value-Added Networks (VANs)</Typography></li>
                <li><Typography variant="body2">Direct TCP/IP connections</Typography></li>
                <li><Typography variant="body2">AS2 secure file transfer</Typography></li>
                <li><Typography variant="body2">HTTPS web services</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Security Features:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">TLS 1.2+ encryption</Typography></li>
                <li><Typography variant="body2">Digital certificate authentication</Typography></li>
                <li><Typography variant="body2">Password complexity requirements</Typography></li>
                <li><Typography variant="body2">Session timeout controls</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ABI Message Types */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Common ABI Message Types
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="Type 01 - Entry Summary" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Standard consumption entry</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Type 11 - Entry/Immediate Delivery" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Cargo release request</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Type 86 - Section 321" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">De minimis entry</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="QP - Query/Response" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Entry status lookup</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="RL - Release" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Cargo release notification</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="BL - Bill" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Duty bill statement</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="LQ - Liquidation" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Entry liquidation notice</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="RJ - Reject" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Entry rejection message</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ABISystemDetail;
